import { useState } from "react";
import { Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { enqueue, isNetworkError } from "../../lib/offlineQueue";
import { ContactFields, EMPTY_CONTACT } from "./ContactFields";
import { CardThumb } from "./CardThumb";

// Bottom sheet to add a contact by hand or edit/delete an existing one.
export const ContactEditSheet = ({ eventId, contact, onSaved, onDeleted, onClose }) => {
  const isNew = !contact?.id;
  const [form, setForm] = useState({ ...EMPTY_CONTACT, ...(contact || {}) });
  const [busy, setBusy] = useState(false);

  const payload = () => ({
    name: form.name, email: form.email.trim() || null, phone: form.phone, company: form.company, job_title: form.job_title,
  });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = isNew
        ? await api.post(`/admin/events/${eventId}/contacts`, { ...payload(), source: "manual" }, { timeout: 15000 })
        : await api.patch(`/admin/event-contacts/${contact.id}`, payload());
      onSaved(data.contact, isNew);
    } catch (err) {
      if (isNew && isNetworkError(err)) {
        await enqueue({ type: "contact", event_id: eventId, payload: { ...payload(), source: "manual" } });
        toast.success("Saved on this phone — it'll upload when you're back online.");
        onClose();
        return;
      }
      toast.error(isNetworkError(err) ? "No signal — edits need internet. Try again later." : formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save contact.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${contact.name || "this contact"}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/event-contacts/${contact.id}`);
      onDeleted(contact.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't delete.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="contact-edit-sheet" onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-brand-cream p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-black text-2xl text-brand-charcoal">{isNew ? "Add contact" : "Edit contact"}</p>
          <button type="button" onClick={onClose} data-testid="contact-edit-close" className="p-2 rounded-full bg-white border border-black/10" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {contact?.needs_review && (
          <p data-testid="contact-edit-review-hint" className="mb-3 rounded-xl bg-brand-gold/20 px-3 py-2 text-[12px] text-brand-charcoal">
            This card was read automatically after syncing. Check the details against the photo, then Save.
          </p>
        )}
        {contact?.card_image_id && <CardThumb fileId={contact.card_image_id} testid="contact-edit-card" className="w-full max-h-56 rounded-2xl mb-4 object-contain bg-white" />}
        <ContactFields value={form} onChange={setForm} testidPrefix="contact-edit" />
        <div className={`mt-5 grid gap-2 ${isNew ? "grid-cols-1" : "grid-cols-[auto_1fr]"}`}>
          {!isNew && (
            <button type="button" onClick={remove} disabled={busy} data-testid="contact-edit-delete" className="h-12 w-12 rounded-full border border-red-200 text-red-600 inline-flex items-center justify-center" aria-label="Delete contact">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={save} disabled={busy} data-testid="contact-edit-save" className="h-12 rounded-full bg-brand-charcoal text-white font-bold uppercase tracking-[0.1em] text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactEditSheet;
